import { useMutation } from "@apollo/client";
import { routedClient } from "react-migration/lib/persistence/apollo";
import { useSnapshot } from "valtio";
import { LogEventCreatedSiteEventLocation, logEventV2 } from "react-migration/lib/util/logEventV2";
import createSites from "../graphql/createSites.gql";
import { Site } from "../typings/Site";
import { mapStore } from "src/js/stores/map/store";
import { refreshSiteGeometryStore } from "src/js/stores/siteGeometry/actions/refreshSiteGeometryStore";
import { SaveSiteLayer } from "react-migration/domains/sites/typings/SaveSiteLayer";

export type CreatedSite = Record<string, unknown> & {
  _id: string;
  _stage?:
    | {
        _id: string;
      }
    | undefined
    | null;
};

export interface CreateSitesQueryData {
  createSites: {
    id: string;
    saved_site: CreatedSite;
    geometry_info: "OK" | "BAD_GEOMETRY" | "NO_POLYGON";
  }[];
}

export interface CreateSitesQueryVariables {
  sites: {
    geometry: GeoJSON.Point | GeoJSON.Polygon | GeoJSON.MultiPolygon;
    source: { layer: SaveSiteLayer };
  }[];
  stageId?: string;
}

interface UseSaveSitesMutationArgs {
  sites: Site[];
  stageId?: string;
  source: LogEventCreatedSiteEventLocation;
  selectionType?: string;
  cardName?: string;
  onCompleted?: () => void;
}

export function useSaveSiteMutation({
  sites,
  stageId,
  source,
  selectionType,
  cardName,
  onCompleted,
}: UseSaveSitesMutationArgs) {
  const { settings } = useSnapshot(mapStore);
  const currentBundleId = settings.activeLayer;

  return useMutation<CreateSitesQueryData, CreateSitesQueryVariables>(createSites, {
    client: routedClient,
    variables: {
      sites: sites,
      stageId,
    },
    onCompleted() {
      if (mapStore.googleMap) {
        refreshSiteGeometryStore(mapStore.googleMap);
      }

      logEventV2({ name: "Sites layer button click", properties: { type: "save" } });
      logEventV2({
        name: "Created site",
        properties: {
          source: currentBundleId,
          eventLocation: source,
          selectionType,
          cardName,
        },
      });

      onCompleted?.();
    },
  });
}
